import { doc, onSnapshot } from 'firebase/firestore';
import protobuf from 'protobufjs';
import { db } from './firebase';

// BLE service / characteristic UUIDs — must match BLEServices exactly
const WORLD_SERVICE = 'a88cffe0-c754-47e8-bad7-1a89477073ac';
const WORLD_TX_CHAR = '10b916f9-02e7-42e1-a57c-0e4c411fe84d';
const PREFS_SERVICE = 'bf1926cb-c538-407a-aad4-0cf304c810e0';
const PREFS_TX_CHAR = '2ef9a760-3a90-4c21-9969-da37a84b8356';

// World-state opcodes (first byte of the characteristic payload)
const OP_SNAPSHOT       = 1;
const OP_STATS          = 2;
const OP_WAYPOINT       = 3;
const OP_WAYPOINT_REMOVED = 4;

let _root = null;

async function getRoot() {
  if (_root) return _root;
  // Proto files are served from public/protos/. requests.proto imports telemetry + stats,
  // waypoints imports telemetry — load all explicitly so protobufjs resolves them.
  _root = await protobuf.load([
    '/protos/telemetry.proto',
    '/protos/stats.proto',
    '/protos/requests.proto',
    '/protos/waypoints.proto',
    '/protos/preferences.proto',
  ]);
  return _root;
}

// Convert a Firestore Bytes value to Uint8Array.
function toBytes(data) {
  if (!data) return new Uint8Array(0);
  if (data instanceof Uint8Array) return data;
  if (typeof data.toUint8Array === 'function') return data.toUint8Array();
  return new Uint8Array(0);
}

function decodeWorldState(root, record) {
  if (!record || record.writer !== 'app') return null;
  const raw = toBytes(record.data);
  if (raw.length < 2) return null;
  const opcode = raw[0];
  const payload = raw.slice(1);
  try {
    switch (opcode) {
      case OP_SNAPSHOT:
        return { type: 'snapshot', data: root.lookupType('rekkie.GroupTelemetrySnapshot').decode(payload) };
      case OP_STATS:
        return { type: 'stats', data: root.lookupType('rekkie.GroupStatsUpdate').decode(payload) };
      case OP_WAYPOINT:
        return { type: 'waypoint', data: root.lookupType('rekkie.WaypointUpdate').decode(payload) };
      case OP_WAYPOINT_REMOVED:
        return { type: 'waypointRemoved' };
      default:
        return null;
    }
  } catch (e) {
    console.warn('[virtual-goggles] world-state decode error', e);
    return null;
  }
}

function decodePrefs(root, record) {
  if (!record || record.writer !== 'app') return null;
  const raw = toBytes(record.data);
  if (!raw.length) return null;
  try {
    return root.lookupType('rekkie.GogglesPreferencesRequest').decode(raw);
  } catch (e) {
    console.warn('[virtual-goggles] prefs decode error', e);
    return null;
  }
}

/**
 * Subscribe to the virtual_goggles/{uid} document.
 * Calls onUpdate on every snapshot with decoded world-state and preferences.
 * Returns an unsubscribe function.
 */
export function subscribe(uid, onUpdate) {
  let root = null;
  getRoot().then(r => { root = r; }).catch(e => console.error('[virtual-goggles] proto load', e));

  return onSnapshot(doc(db, 'virtual_goggles', uid), async (snap) => {
    if (!root) {
      try { root = await getRoot(); } catch (e) { /* will retry next snapshot */ }
    }

    if (!snap.exists()) {
      onUpdate({ exists: false });
      return;
    }

    const d = snap.data();
    const chars = d.characteristics || {};
    const worldRecord = chars[WORLD_SERVICE]?.[WORLD_TX_CHAR];
    const prefsRecord = chars[PREFS_SERVICE]?.[PREFS_TX_CHAR];

    onUpdate({
      exists: true,
      powered_on: d.powered_on ?? false,
      pairing_pin: d.pairing_pin ?? '',
      worldState: root ? decodeWorldState(root, worldRecord) : null,
      preferences: root ? decodePrefs(root, prefsRecord) : null,
    });
  });
}

async function apiFetch(method, path, body, getIdToken) {
  const token = await getIdToken();
  const hasBody = body !== null;
  const resp = await fetch(`/api/virtual-goggles${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
    },
    body: hasBody ? JSON.stringify(body) : null,
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    throw new Error(err.message || resp.statusText);
  }
  return resp.json();
}

export const turnOn      = (getIdToken) => apiFetch('POST', '/on', {}, getIdToken);
export const turnOff     = (getIdToken) => apiFetch('POST', '/off', {}, getIdToken);
export const setPin      = (pin, getIdToken) => apiFetch('POST', '/pin', { pin }, getIdToken);
export const setBuild    = (params, getIdToken) => apiFetch('POST', '/build', params, getIdToken);
export const setBattery  = (level, getIdToken) => apiFetch('POST', '/battery', { level }, getIdToken);
export const deleteDoc   = (getIdToken) => apiFetch('DELETE', '', null, getIdToken);

/** Used in pagehide handler where we can't await — fires and forgets with keepalive. */
export function deleteWithKeepalive(token) {
  if (!token) return;
  fetch('/api/virtual-goggles', {
    method: 'DELETE',
    keepalive: true,
    headers: { Authorization: `Bearer ${token}` },
  }).catch(() => {});
}
